import { Prisma, prisma, ChatParticipantType, MessageType } from "client";
import { getChatBySenderAndReceiverIds } from "../chat";
import { sendExpoNotification } from "@sajilni/utils/expoNotification";
import { getDeviceTokensByUser } from "services/deviceToken";

export const createMessage = async ({
  senderId,
  senderType,
  content,
  type,
  eventId,
  receiverId,
  receiverType,
  lang = "en",
  chatName,
  chatId: existingChatId,
  senderIdsForLookup,
}: {
  senderId: string;
  senderType: ChatParticipantType;
  content: string;
  type: MessageType;
  eventId: string;
  receiverId: string;
  receiverType: ChatParticipantType;
  lang: string;
  chatName?: string;
  /** Prefer an already-open thread — avoids a second chat when ids differ (user vs company). */
  chatId?: string;
  senderIdsForLookup?: string[];
}) => {
  try {
    const messageAt = new Date();
    let chatId = existingChatId;
    let resolvedSenderId = senderId;
    const lookupIds = [
      ...new Set(
        (senderIdsForLookup?.length ? senderIdsForLookup : [senderId]).filter(
          Boolean
        )
      ),
    ];

    if (chatId) {
      // Thread must include the receiver AND one of our identities (blocks chatId ACL bypass).
      const owned = await prisma.chat.findFirst({
        where: {
          id: chatId,
          eventId,
          AND: [
            {
              participants: {
                some: { participantId: { in: lookupIds } },
              },
            },
            {
              participants: {
                some: { participantId: receiverId },
              },
            },
          ],
        },
        select: {
          id: true,
          participants: { select: { participantId: true } },
        },
      });
      if (!owned) {
        chatId = undefined;
      } else {
        const onThread = new Set(
          owned.participants.map((p) => p.participantId)
        );
        // Write as an id already on the thread (prefer client senderId).
        if (!onThread.has(resolvedSenderId)) {
          const alias = lookupIds.find((id) => onThread.has(id));
          if (alias) resolvedSenderId = alias;
          else chatId = undefined;
        }
      }
    }

    if (!chatId) {
      const checkExistingChat = await getChatBySenderAndReceiverIds(
        lookupIds,
        eventId,
        receiverId
      );
      if (!checkExistingChat) {
        const newChat = await prisma.chat.create({
          data: {
            name: chatName || "New Chat",
            lastMessageAt: messageAt,
            participants: {
              createMany: {
                data: [
                  {
                    participantId: resolvedSenderId,
                    type: senderType,
                  },
                  {
                    participantId: receiverId,
                    type: receiverType,
                  },
                ],
              },
            },
            eventId,
          },
        });

        chatId = newChat.id;
      } else {
        chatId = checkExistingChat.id;
        const onThread = new Set(
          checkExistingChat.participants.map((p) => p.participantId)
        );
        if (!onThread.has(resolvedSenderId)) {
          const alias = lookupIds.find((id) => onThread.has(id));
          if (alias) resolvedSenderId = alias;
        }
      }
    }

    const message = await prisma.$transaction(async (tx) => {
      const createdMessage = await tx.message.create({
        data: {
          content,
          type,
          createdAt: messageAt,
          senderId: resolvedSenderId,
          senderType,
          chatId,
        },
        include: {
          user: true,
          sponsor: {
            include: {
              translations: { where: { language: { code: lang } } },
            },
          },
          exhibitor: {
            include: {
              translations: { where: { language: { code: lang } } },
            },
          },
        },
      });
      await tx.chat.updateMany({
        where: {
          id: chatId,
          OR: [
            { lastMessageAt: null },
            { lastMessageAt: { isSet: false } },
            { lastMessageAt: { lte: messageAt } },
          ],
        },
        data: { lastMessageAt: messageAt },
      });
      return createdMessage;
    });
    const senderName =
      message?.user?.name ||
      message?.sponsor?.translations?.[0]?.name ||
      message.exhibitor?.translations?.[0]?.name;
    const expoPushTokens = await getDeviceTokensByUser(receiverId, eventId);

    await sendExpoNotification(
      receiverId,
      expoPushTokens,
      `Message From ${senderName}`,
      {
        type: "chat",
        chat_id: chatId,
        sender_id: resolvedSenderId,
      },
      content
    );
    return message;
  } catch (error) {
    console.error("Error creating message:", error);
    throw new Error("Failed to create message");
  }
};
export const getChatsByUserId = async (userId: string) => {
  try {
    // Fetch chats where the user is a participant
    const chats = await prisma.chat.findMany({
      where: {
        participants: {
          some: {
            participantId: userId,
          },
        },
      },
      include: {
        participants: {
          include: {
            exhibitor: {
              include: { translations: true },
            },
            sponsor: { include: { translations: true } },
            user: true,
          },
        },
        messages: {
          orderBy: {
            createdAt: "desc",
          },
          take: 1,
        },
      },
    });

    return chats;
  } catch (error) {
    console.error("Error fetching chats for user:", error);
    throw new Error("Failed to fetch chats");
  }
};

export const countUnreadMessages = async (
  participantIds: string | string[],
  eventId?: string,
  organizerUserId?: string
) => {
  const ids = (
    Array.isArray(participantIds) ? participantIds : [participantIds]
  ).filter(Boolean);
  if (!ids.length) return 0;

  const companyIds = organizerUserId
    ? ids.filter((id) => id !== organizerUserId)
    : ids;
  const chats = await prisma.chat.findMany({
    where: {
      ...(eventId ? { eventId } : {}),
      ...(organizerUserId
        ? {
            OR: [
              ...(companyIds.length
                ? [
                    {
                      participants: {
                        some: { participantId: { in: companyIds } },
                      },
                    },
                  ]
                : []),
              {
                AND: [
                  {
                    participants: {
                      some: { participantId: organizerUserId },
                    },
                  },
                  {
                    participants: {
                      some: { type: ChatParticipantType.ORGANIZATION },
                    },
                  },
                ],
              },
            ],
          }
        : { participants: { some: { participantId: { in: ids } } } }),
    },
    select: { id: true },
  });
  if (!chats.length) return 0;

  return prisma.message.count({
    where: {
      chatId: { in: chats.map((chat) => chat.id) },
      isRead: false,
      senderId: { notIn: ids },
    },
  });
};

/** Incoming messages in this thread (sender is not the viewer). Indexed on chatId. */
export const markChatMessagesRead = async (
  chatId: string,
  viewerIds: string | string[]
) => {
  const ids = (Array.isArray(viewerIds) ? viewerIds : [viewerIds]).filter(
    Boolean
  );
  if (!chatId || !ids.length) return;
  await prisma.message.updateMany({
    where: {
      chatId,
      isRead: false,
      senderId: { notIn: ids },
    },
    data: { isRead: true },
  });
};

export const getChatMessages = async ({
  page = 1,
  take = 100,
  chatId,
  eventId,
  lang = "en",
}: {
  chatId: string;
  eventId?: string;
  take?: number;
  page?: number;
  lang?: string;
}) => {
  try {
    const limit = Math.min(Math.max(take || 100, 1), 100);
    const messages = await prisma.message.findMany({
      where: {
        chatId,
      },
      include: {
        exhibitor: {
          include: {
            translations: { where: { language: { code: lang } } },
          },
        },
        user: {
          include: {
            profiles: {
              ...(eventId ? { where: { eventId } } : {}),
              orderBy: { createdAt: "desc" },
              take: 1,
            },
          },
        },
        sponsor: {
          include: {
            translations: { where: { language: { code: lang } } },
          },
        },
      },
      orderBy: {
        createdAt: "asc",
      },
      take: limit,
      skip: (page - 1) * limit,
    });
    const total = await prisma.message.count({ where: { chatId } });
    return {
      messages,
      pagination: {
        total,
        page,
        take: limit,
      },
    };
  } catch (error) {
    console.error("Error fetching messages:", error);
    throw new Error("Failed to fetch messages");
  }
};
